import { z } from 'zod';

// en-US tokens per ADR 0009; labels in `@immo/i18n` `pages.workspace.deals.mandate-type.*`.
export const MANDATE_TYPES = ['SIMPLE_BROKERAGE', 'SIMPLE_EXCLUSIVE', 'QUALIFIED_EXCLUSIVE'] as const;
export const MandateTypeSchema = z.enum(MANDATE_TYPES);
export type MandateType = z.infer<typeof MandateTypeSchema>;

/** Provision Verkäufer / Käufer: percent of the sale price, net of VAT, 0–100, at most two decimals (PRD Fields). */
export const MandateCommissionPercentSchema = z.number().min(0).max(100).multipleOf(0.01);
/** Laufzeit: whole months, 1–24 (PRD Resolved Decision 5). */
export const MandateDurationMonthsSchema = z.number().int().min(1).max(24);

/**
 * „Läuft bis“ = Tätigkeitsaufnahme + Laufzeit in calendar months (PRD: 01.11.2026 + 3 → 01.02.2027).
 * A start day the end month lacks clamps to that month's last day (31.01. + 1 → 28./29.02.).
 * Null when either input is null. Pure, date-only (`YYYY-MM-DD`), no time zone involved.
 */
export function mandateEndsOn(startDate: string | null, durationMonths: number | null): string | null {
  if (startDate === null || durationMonths === null) return null;

  const [year, month, day] = startDate.split('-').map(Number) as [number, number, number];
  const monthIndex = month - 1 + durationMonths;
  const endYear = year + Math.floor(monthIndex / 12);
  const endMonth = monthIndex % 12;
  const lastDay = new Date(Date.UTC(endYear, endMonth + 1, 0)).getUTCDate();

  return `${endYear}-${String(endMonth + 1).padStart(2, '0')}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
}

/** Erwartete Maklergebühr (IMM-1150 AC 13, IMM-1380): both sides plus the Objekt's fixed amount, an empty commission counting as 0; null without a price. Unrounded. */
export function expectedCommissionOf(
  price: number | null,
  terms: {
    readonly sellerCommissionPercent: number | null;
    readonly buyerCommissionPercent: number | null;
    readonly buyerCommissionAmountNet: number | null;
  },
): number | null {
  if (price === null) return null;

  const percent = (terms.sellerCommissionPercent ?? 0) + (terms.buyerCommissionPercent ?? 0);

  return (price * percent) / 100 + (terms.buyerCommissionAmountNet ?? 0);
}
